/**
 * useAuth - Authentication Hook
 *
 * Custom React hook for accessing authentication state and actions.
 * Provides a clean, type-safe interface to the AuthContext.
 *
 * Usage:
 * ```tsx
 * function MyComponent() {
 *   const { isAuthenticated, user, login, logout } = useAuth();
 *
 *   if (!isAuthenticated) {
 *     return <button onClick={() => login()}>Sign In</button>;
 *   }
 *
 *   return (
 *     <div>
 *       <p>Welcome, {user?.displayName}</p>
 *       <button onClick={logout}>Sign Out</button>
 *     </div>
 *   );
 * }
 * ```
 */

import { useContext } from 'react';
import { AuthContext, type AuthContextValue } from './AuthContext';

/**
 * useAuth Hook
 *
 * Access authentication state and actions from any component
 * within the AuthProvider tree.
 *
 * @returns AuthContextValue containing state and actions
 * @throws Error if used outside of AuthProvider
 *
 * @example
 * ```tsx
 * const { isAuthenticated, user, roles, login, logout } = useAuth();
 * ```
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  // Context will always have a value due to default value in createContext,
  // but we can detect if we're outside the provider by checking for loading
  // state on initial render combined with other indicators

  return context;
}

/**
 * useAuthState - Access only authentication state
 *
 * Use this when you only need to read state, not perform actions.
 * This can help with component re-render optimization.
 *
 * @returns Authentication state (isLoading, isAuthenticated, user, roles, error)
 */
export function useAuthState() {
  const { isLoading, isAuthenticated, user, roles, error, providerType } =
    useAuth();

  return {
    isLoading,
    isAuthenticated,
    user,
    roles,
    error,
    providerType,
  };
}

/**
 * useAuthActions - Access only authentication actions
 *
 * Use this when you only need to perform actions, not read state.
 * This can help with component re-render optimization.
 *
 * @returns Authentication actions (login, logout, refreshUser, clearError, switchProvider)
 */
export function useAuthActions() {
  const { login, logout, refreshUser, clearError, switchProvider } = useAuth();

  return {
    login,
    logout,
    refreshUser,
    clearError,
    switchProvider,
  };
}

/**
 * useIsAuthenticated - Simple boolean check for authentication status
 *
 * Use this for simple conditional rendering based on auth status.
 *
 * @returns Object with isAuthenticated and isLoading booleans
 */
export function useIsAuthenticated() {
  const { isAuthenticated, isLoading } = useAuth();

  return {
    isAuthenticated,
    isLoading,
  };
}

/**
 * useRoles - Access current user's roles
 *
 * Provides role-checking utilities for authorization.
 *
 * @returns Object with roles array and helper functions
 */
export function useRoles() {
  const { roles, isLoading } = useAuth();

  return {
    /** All roles assigned to the current user */
    roles,

    /** Whether auth is still loading */
    isLoading,

    /**
     * Check if user has a specific role (case-insensitive)
     */
    hasRole: (role: string): boolean =>
      roles.some((r) => r.toLowerCase() === role.toLowerCase()),

    /**
     * Check if user has any of the specified roles (case-insensitive)
     */
    hasAnyRole: (rolesToCheck: string[]): boolean =>
      rolesToCheck.some((role) =>
        roles.some((r) => r.toLowerCase() === role.toLowerCase())
      ),

    /**
     * Check if user has all of the specified roles (case-insensitive)
     */
    hasAllRoles: (rolesToCheck: string[]): boolean =>
      rolesToCheck.every((role) =>
        roles.some((r) => r.toLowerCase() === role.toLowerCase())
      ),

    /**
     * Check if user is an admin
     */
    isAdmin: roles.some((r) => r.toLowerCase() === 'admin'),
  };
}
